import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import domPurify from 'dompurify'
import { Moment } from 'moment'
import { ChecklistItemWithResourceInfo, CustomTaskLink } from 'TypeDefs/Goal'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { useAppState } from 'Context/AppContext/AppContext'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { GoalType, User } from 'Generated/graphql-types'
import { SkeletonLoader } from 'Components/SharedComponents/SkeletonLoader/SkeletonLoader'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import {
  Clickable,
  Status,
  TaskActions,
  TaskContainer,
  TaskDetails,
  TaskItemRow,
  TaskTitle,
  SupplementalItem,
} from './TaskItem.styled'
import TaskMenu from './TaskMenu/TaskMenu'
import AcademyResourceTaskItem from '../../../Impact/GoalPage/Tasks/ResourceTasks/AcademyResourceTaskItem'
import { AssignTaskOwner } from './AssignTaskOwner'
import {
  ExternalLink,
  TaskItemRowTitle,
} from '../../../Impact/GoalPage/Tasks/ResourceTasks/AcademyResourceTasks.styles'
import { DatePicker } from '../../../../SharedComponents/DatePicker/DatePicker'

type ITaskItemProps = {
  item: ChecklistItemWithResourceInfo
  isResourceLoading?: boolean
  onChange: (itemInfo: ChecklistItemWithResourceInfo) => Promise<void>
  onDeleteTask: (task: ChecklistItemWithResourceInfo) => void
  eventProps: {
    goalType: GoalType
    templateLibraryId: number | null
    initiativeId?: number
    goalId: number
    isGoalSharedWithFunder?: boolean | undefined
  }
  onDuplicateTask: (item: ChecklistItemWithResourceInfo) => void
  onEditTask: () => void
}

export const MINIMUM_PROTOCOL = 'http'

const TaskItem = ({
  item,
  isResourceLoading,
  onChange,
  onDeleteTask,
  eventProps,
  onDuplicateTask,
  onEditTask,
}: ITaskItemProps) => {
  const location = useLocation()
  const taskIdFromParams = new URLSearchParams(location.search).get('taskId')
  const isTaskIdParamMatch = !!(
    item.id &&
    taskIdFromParams &&
    item.id.toString() === taskIdFromParams
  )
  const { id, isCompleted, title, owner: currentTaskOwner } = item
  const [completed, setCompleted] = useState(!!isCompleted)
  const [isTaskMenuOpen, setIsTaskMenuOpen] = useState(false)
  const [isTaskOwnerPopoverOpen, setIsTaskOwnerPopoverOpen] = useState(false)
  const [anchorElAcademyLinks, setAnchorElAcademyLinks] =
    useState<null | HTMLElement>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [owner, setOwner] = useState<User | null | undefined>(undefined)
  const { teamMap = {} } = useNonprofitOrgTeam()
  const [itemWithResourceInfo, setItemWithResourceInfo] =
    useState<ChecklistItemWithResourceInfo>({ ...item })

  const {
    userConfig: { id: userId },
  } = useAppState()

  const {
    goalType,
    templateLibraryId,
    goalId,
    initiativeId,
    isGoalSharedWithFunder,
  } = eventProps
  const eventData = {
    userId,
    goalId,
    initiativeId,
    goalType,
    templateLibraryId,
    isGoalSharedWithFunder,
  }

  useEffect(() => {
    const verticalElipsis = document.getElementById(`taskListMenu${id}`)
    setMenuAnchor(verticalElipsis)
  }, [id])

  useEffect(() => {
    setItemWithResourceInfo((currentItem) => ({ ...currentItem, ...item }))
  }, [item])

  const dispatchTaskAcademyLinkClicked = () => {
    analytics.trackActionCreator(initiatives.Goal_Task_Academy_Link_Clicked)({
      ...eventData,
      taskId: id,
      resourceId: itemWithResourceInfo.resource?.id,
    })
  }

  const handleChangeStatus = async (isComplete: boolean) => {
    setCompleted(isComplete)
    const currentOwner = item?.owner?.id
    const assigneeUserEmail = currentOwner ? teamMap[currentOwner].email : null
    await onChange({ ...itemWithResourceInfo, isCompleted: isComplete })
    if (isComplete) {
      analytics.trackActionCreator(goals.Goal_Task_Completed)({
        ...eventData,
        assigneeUserEmail,
        resourceId: itemWithResourceInfo.resource?.id || null,
        taskDescription: item.title,
        taskId: id,
        taskStatus: true,
        taskUpdatedDate: item.updatedAt.substring(0, 10),
        dueDate: item.dueDate ? item.dueDate.slice(0, 10) : null,
      })
    }
  }

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAcademyLinks(event.currentTarget)
    setIsTaskOwnerPopoverOpen(true)
  }

  const handlePopoverClose = () => {
    setAnchorElAcademyLinks(null)
    setIsTaskOwnerPopoverOpen(false)
  }

  const handleChangeOwner = async (newOwner: number) => {
    if (newOwner === 0) {
      setIsTaskOwnerPopoverOpen(false)
      setOwner(null)
      await onChange({ ...itemWithResourceInfo, owner: null })
    }

    if (newOwner) {
      const selectedOwner = { ...teamMap[newOwner], id: newOwner }
      setIsTaskOwnerPopoverOpen(false)
      setOwner(selectedOwner)
      await onChange({
        ...itemWithResourceInfo,
        owner: selectedOwner,
      })

      analytics.trackActionCreator(goals.Goal_Task_Assigned)({
        ...eventData,
        assigneeUserEmail: selectedOwner.email,
        assigneeUserId: newOwner,
        assignerUserId: userId,
        taskDescription: item.title,
        taskId: id,
        taskStatus: item.isCompleted ? 'complete' : 'pending',
        taskUpdatedDate: item.updatedAt.substring(0, 10), // e.g. 2024-01-12
        goalUpdatedDate: item.updatedAt.substring(0, 10),
        goalTaskAssignationStatus: newOwner ? 'complete' : 'pending',
        dueDate: item.dueDate ? item.dueDate.slice(0, 10) : null,
      })
      if (userId !== newOwner) {
        analytics.trackActionCreator(
          initiatives.Goal_Task_Assigned_Notification_Sent
        )({
          ...eventData,
          taskId: id,
        })
      }
    }
  }

  const handleDeleteItem = async () => {
    if (item.id) onDeleteTask(item)
  }

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Check if the click target is one of the excluded child elements
    const excludedElements = ['INPUT', 'P', 'BUTTON']
    const element = event.target as HTMLElement

    if (excludedElements.includes(element.tagName)) return

    setIsTaskMenuOpen(true)
    event.stopPropagation()
  }

  const handleSaveTaskLink = async (linkText: string, linkUrl: string) => {
    let newLinkUrl = linkUrl
    // Add minimum protocol check for best UX since we've decided not to do true validation
    if (linkUrl && !linkUrl.includes(MINIMUM_PROTOCOL))
      newLinkUrl = `https://${linkUrl}`

    await onChange({ ...itemWithResourceInfo, linkText, linkUrl: newLinkUrl })
  }

  const handleDuplicateTask = () => {
    onDuplicateTask(itemWithResourceInfo)
    setIsTaskMenuOpen(false)
  }

  const taskLink = itemWithResourceInfo.linkUrl
    ? ({
        linkText: itemWithResourceInfo.linkText,
        linkUrl: itemWithResourceInfo.linkUrl,
      } as CustomTaskLink)
    : null

  const handleDateSelected = (date: Moment | null) => {
    const newDueDate = date ? date.format('YYYY-MM-DD') : null
    if (newDueDate) {
      analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Submitted)({
        dueDate: newDueDate,
        goalId,
        goalType,
        initiativeId,
        isGoalSharedWithFunder,
        linkText: item.linkText,
        linkUrl: item.linkUrl,
        taskDescription: item.title,
        taskId: id,
        taskStatus: item.isCompleted ? 'complete' : 'pending',
        taskUpdatedDate: item.updatedAt.substring(0, 10),
        templateLibraryId,
        resourceId: item.resource?.id,
        userId,
      })
    } else {
      analytics.trackActionCreator(
        initiatives.Goal_Task_DueDate_Delete_Clicked
      )({
        ...eventData,
        taskDescription: item.title,
        taskId: id,
        taskStatus: item.isCompleted ? 'complete' : 'pending',
        taskUpdatedDate: item.updatedAt.substring(0, 10),
        dueDate: newDueDate,
        resourceId: item.resource?.id,
        linkText: item.linkText,
        linkUrl: item.linkUrl,
      })
    }
    const updatedItemInfo = {
      ...itemWithResourceInfo,
      dueDate: newDueDate,
    }

    setItemWithResourceInfo(updatedItemInfo)
    onChange(updatedItemInfo)
  }

  const handleEditTask = () => {
    onEditTask()
    setIsTaskMenuOpen(false)
  }

  return (
    <TaskContainer shouldHighlight={isTaskIdParamMatch}>
      <Clickable onClick={handleClickMenu}>
        <TaskDetails>
          <TaskItemRow>
            <Status
              disabled={title.length === 0}
              checked={completed}
              onChange={({ target: { checked } }) =>
                handleChangeStatus(checked)
              }
            />
            <TaskTitle titleLength={title.length} completed={completed}>
              {title}
            </TaskTitle>
          </TaskItemRow>

          {isResourceLoading && (
            <SkeletonLoader
              styles={{
                height: '10px',
                width: '50%',
              }}
            />
          )}
          {!isResourceLoading && itemWithResourceInfo.resource && (
            <SupplementalItem>
              <AcademyResourceTaskItem
                resource={itemWithResourceInfo.resource}
                completed={completed}
                dispatchTaskAcademyLinkClicked={dispatchTaskAcademyLinkClicked}
              />
            </SupplementalItem>
          )}
          {taskLink && (
            <SupplementalItem>
              <ExternalLink
                href={domPurify.sanitize(taskLink.linkUrl)}
                target='_blank'
                rel='noopener noreferrer'
              >
                <TaskItemRowTitle>
                  {domPurify.sanitize(taskLink.linkText)}
                </TaskItemRowTitle>
              </ExternalLink>
            </SupplementalItem>
          )}
        </TaskDetails>
      </Clickable>
      <TaskActions>
        <DatePicker
          date={itemWithResourceInfo.dueDate}
          label='Due Date'
          onDateSelected={handleDateSelected}
        />
        <AssignTaskOwner
          disabled={title.length === 0}
          isPopoverOpen={isTaskOwnerPopoverOpen}
          onAssignOwner={handleChangeOwner}
          onClickOwnerAvatar={handlePopoverOpen}
          onClosePopover={handlePopoverClose}
          owner={owner || owner === null ? owner : currentTaskOwner || null}
          popoverAnchorEl={anchorElAcademyLinks}
        />
        <TaskMenu
          isTaskMenuOpen={isTaskMenuOpen}
          menuAnchor={menuAnchor}
          onClick={handleClickMenu}
          onClose={() => setIsTaskMenuOpen(false)}
          onEditTask={handleEditTask}
          onDeleteTask={handleDeleteItem}
          onSaveTaskLink={handleSaveTaskLink}
          onDuplicateTask={handleDuplicateTask}
          taskId={id}
        />
      </TaskActions>
    </TaskContainer>
  )
}

export default TaskItem
